<template>
  <div class="flex flex-col pt-8">
    <div
      class="flex md:items-center flex-col md:flex-row mb-8"
    >
      <transition
        enter-active-class="transition-all duration-300 ease-out"
        leave-active-class="transition-all duration-300 ease-out"
        enter-class="opacity-0 invisible"
        enter-to-class="opacity-100 visible"
        leave-class="opacity-100 visible"
        leave-to-class="opacity-0 invisible"
        mode="out-in"
      >
        <h3
          v-if="!store.state.loading"
          class="text-3xl mb-4 md:mb-0"
          v-html="heading"
        />
      </transition>
      <div
        class="flex flex-col md:flex-row md:ltr:ml-auto md:rtl:mr-auto min-w-full md:min-w-0 md:items-center"
      >
        <span class="text-xs">{{ model.sortOrderLabelText }}</span>
        <Dropdown
          v-model="sortOrder"
          class="mt-2 md:mt-0 md:ltr:ml-3 md:rtl:mr-3 min-w-full md:min-w-116"
          :items="sortingItems"
          :disabled="!initialCards && !store.state.gridCards.length"
          @onClick="onSelectorChange"
        />
      </div>
    </div>
    <transition
      enter-active-class="transition-all duration-300 ease-out"
      leave-active-class="transition-all duration-300 ease-out"
      enter-class="opacity-0 invisible"
      enter-to-class="opacity-100 visible"
      leave-class="opacity-100 visible"
      leave-to-class="opacity-0 invisible"
    >
      <div ref="rendered">
        <div
          v-if="initialCards"
          key="initialContent"
          v-start-with-html="initialCards"
          class="grid gap-6 grid-cols-2 md:grid-cols-4 mb-12"
        >
          <component
            :is="getComponent(card)"
            v-for="card in store.state.gridCards"
            :key="card.uid || card.kicker"
            :model="card"
            :tracking-obj="trackingObj"
          />
        </div>
        <div
          v-if="!initialCards && store.state.gridCards.length"
          key="content"
          class="grid gap-6 grid-cols-2 md:grid-cols-4 mb-12"
        >
          <component
            :is="getComponent(card)"
            v-for="card in store.state.gridCards"
            :key="card.uid || card.kicker"
            :model="card"
            :tracking-obj="trackingObj"
          />
        </div>
      </div>
    </transition>
    <div
      v-if="!initialCards && !store.state.gridCards.length && !store.state.loading"
      class="max-w-3xl mt-4 mb-8 md:mb-20 ml-auto mr-auto text-center"
    >
      <IconNoResults
        class="ml-auto mr-auto mb-8"
      />
      <div class="flex flex-col">
        <h3
          v-if="model.noRecipeResultsTitle"
          class="text-h4 mb-6"
        >
          {{ model.noRecipeResultsTitle }}
        </h3>
        <span
          v-if="model.noRecipeResultsDescription"
          class="text-sm"
        >
          {{ model.noRecipeResultsDescription }}
        </span>
      </div>
    </div>
    <div
      v-if="store.state.loadingMoreRecipes || store.state.loading"
      class="text-center mb-12 w-16 h-16 mx-auto"
    >
      <svg viewBox="0 0 50 50" class="new-spinner">
        <circle
          class="path"
          cx="25"
          cy="25"
          r="20"
          fill="none"
          stroke-width="5"
        />
      </svg>
    </div>
  </div>
</template>

<script>

import IconNoResults from '@/assets/icons/food-restaurant.svg'
import store from '@/components/facet/store'
import updateUrl from '@/components/facet/update-url'
import utils from '@/vue-utils'


import tracking from '@/helpers/tracking'
import Dropdown from '@/components/facet/Dropdown.vue'
import FacetRecipeCard from '@/components/facet/FacetRecipeCard.vue'
import FacetRelatedCategories from '@/components/facet/FacetRelatedCategories.vue'

export default {
  components: {
    Dropdown,
    IconNoResults,
    FacetRecipeCard,
    FacetRelatedCategories
  },
  directives: {
    startWithHtml: {
      inserted(el, binding) {
        el.insertAdjacentHTML('afterbegin', binding.value)
      }
    }
  },
  props: {
    model: {
      type: Object,
      required: true
    },
    slotContent: {
      type: String,
      default: ''
    }
  },
  data() {
    const { sortOrderTexts, recipesCount: pageSize, skip } = this.model
    return {
      store,
      initialCards: this.slotContent.trim(),
      skip,
      pageSize,
      refFacetContainer: null,
      refLoadMoreButton: null,
      sortOrder: sortOrderTexts[0].key,
      hasLoadMoreButton: false
    }
  },
  computed: {
    sortingItems() {
      const { sortOrderTexts } = this.model
      return sortOrderTexts.map(({ key, value }) => ({ name: value, id: key }))
    },
    heading() {
      const { heading } = this.model
      const { totalCount } = this.store.state
      return heading.replace('{0}', `<span>${totalCount}</span>`)
    },
    trackingObj() {
      const { currentTagsAndTerms: currentFilters } = this.store.getters

      return {
        currentFilters,
        placement: 'facet overview page'
      }
    },
    isFacetZero() {
      const { tags, searchTerms } = this.store.state
      return !tags.length && !searchTerms.length
    },
    hideButton() {
      const { totalCount, pageSize, skip } = this.store.state
      return (skip + pageSize) >= totalCount
    }
  },
  watch: {
    'store.state.gridCards': {
      handler(cards) {
        if (cards.length) {
          this.mountChildComponents()
        }
        this.setSmartTags()
      }
    },
    sortOrder() {
      this.initialCards = ''
    },
    'store.state.tags': {
      handler() {
        this.initialCards = ''
      }
    },
    'store.state.searchTerms': {
      handler() {
        this.initialCards = ''
      }
    },
    hideButton() {
      if (this.refLoadMoreButton) {
        this.refLoadMoreButton.classList[this.hideButton ? 'add' : 'remove']('hidden')
      }
    }
  },
  mounted() {
    this.mountChildComponents()
    this.setupInitialState()
    const currentHash = window.location.hash
    this.refLoadMoreButton = document.querySelector('.js-facet-load-more')

    if (this.refLoadMoreButton) {
      this.hasLoadMoreButton = true
      this.refLoadMoreButton.addEventListener('click', (e) => {
        e.preventDefault()
        this.loadMoreRecipes()
      })
    } else {
      this.refFacetContainer = document.querySelector('.js-facet-container')
      window.addEventListener('scroll', this.loadMoreRecipes)
    }
    window.addEventListener('popstate', (event) => {
      if (window.location.hash === currentHash) {
        this.restoreState(event)
      }
    })
    if (this.initialCards) {
      this.addTrackingForServerRenderedElements()
    }
    this.store.dispatch('setLayoutReady')
  },
  methods: {
    setupInitialState() {
      const { skip, pageSize } = this
      const { sortOrderTexts } = this.model
      this.store.dispatch('setTotalCount', { count: this.model.cards.totalCount })
      this.store.dispatch('setPageSize', { pageSize })
      this.store.dispatch('updateSkip', { skip })
      this.store.dispatch('setSortOrder', { sortOrder: sortOrderTexts[0].key })
      this.setSmartTags()
      // save state
      const { state } = this.store
      updateUrl.replace(state)
    },
    getComponent(card) {
      if (card.relatedFacets) {
        return FacetRelatedCategories
      }
      return FacetRecipeCard
    },
    async onSelectorChange(sortOrder) {
      this.store.dispatch('resetCards') // hide cards and show loader first
      this.store.dispatch('showLoader')
      this.store.dispatch('setSortOrder', { sortOrder })
      const { tagUids } = this.store.getters
      await this.store.dispatch('loadCards', { tagUids })
      this.store.dispatch('hideLoader')
    },
    async loadMoreRecipes() {
      let isUIneedMoreItems = true
      if (!this.hasLoadMoreButton) {
        const {
          refFacetContainer
        } = this

        const offset = 2.3
        isUIneedMoreItems = refFacetContainer
          .getBoundingClientRect().bottom <= window.innerHeight * offset
      }

      const { skip, tagUids } = this.store.getters
      const { hasMoreResults, loading, loadingMoreRecipes } = this.store.state

      if (isUIneedMoreItems && !loadingMoreRecipes && hasMoreResults && !loading) {
        await this.store.dispatch('getMoreRecipes', { skip, tagUids })
      }
    },
    async restoreState({ state }) {
      this.store.dispatch('resetCards') // hide cards and show loader first
      this.store.dispatch('setStateFromUrl', { state })
      this.store.dispatch('showLoader')
      const { tagUids, isFacet0 } = this.store.getters
      if (isFacet0) {
        updateUrl.reload()
      } else {
        await this.store.dispatch('loadCards', { tagUids })
        this.store.dispatch('hideLoader')
      }
    },
    addTrackingForServerRenderedElements() {
      this.$refs.rendered.querySelectorAll('.recipe-card').forEach((item) => {
        const recipeName = item.dataset.trackingTitle
        const { currentTagsAndTerms: currentFilters } = this.store.getters
        item.addEventListener('click', () => {
          tracking.recipeClick({
            recipeName,
            currentFilters,
            placement: 'facet overview page'
          })
        })
      })
    },
    setSmartTags() {
      let { gridCards } = this.store.state
      const { totalCount } = this.store.state
      const { items } = this.model.cards

      if (totalCount === 0) {
        this.store.dispatch('setSmartTags', { smartTags: [] })
        return
      }
      if (gridCards.length === 0) {
        gridCards = items
      }
      const relatedContent = gridCards
        .find(({ relatedFacets }) => relatedFacets && relatedFacets.length)

      if (relatedContent) {
        const { relatedFacets } = relatedContent
        const facets = relatedFacets
          .map(({ fullContentCriteriaTags }) => [...fullContentCriteriaTags])

        const { tags } = this.store.state
        let smartTags = []

        facets.forEach((facet) => {
          const hasAllTagsIncluded = tags.every(({ id }) => facet.find((tag) => id === tag.id))
          if (hasAllTagsIncluded && facet.length > tags.length) {
            const facetTagsWithoutSelectedTags = facet
              .filter(({ id }) => !tags.find((tag) => tag.id === id))

            smartTags = [...smartTags, ...facetTagsWithoutSelectedTags]
          }
        })

        smartTags = smartTags
          .filter((item, index) => smartTags.findIndex(({ id }) => item.id === id) === index)

        smartTags = smartTags.slice(0, 5)
        this.store.dispatch('setSmartTags', { smartTags })
      }
    },
    mountChildComponents() {
      utils.mountChildComponents({
        FacetRelatedCategories
      }, null, this.$refs.rendered)
    }
  }
}
</script>
